import React, { useState } from "react";
import { Cpu, RefreshCw } from "lucide-react";
import { postAdvice } from "../../api";
import { timeShort } from "../../lib/format";
import { toast } from "sonner";

const DEFAULT_ADVICE = {
  symbol: "XAUUSD",
  side: "SELL",
  volume: 0.1,
  openLow: 4369,
  openHigh: 4371,
  sl: 4376,
  tps: [4364, 4357, 4350, 4300],
};

export default function AiConsensus({ snapshot }) {
  const [busy, setBusy] = useState(false);
  const [override, setOverride] = useState(null);

  const latest = (snapshot?.signals || []).find((s) => s.aiConsensus)?.aiConsensus;
  const ai = override || latest;
  const models = ai?.models || [];
  const score = ai ? Math.round((ai.agreement || 0) * 100) : 0;
  const confidence = ai ? Math.round((ai.confidence || 0) * 100) : 0;
  const decision = ai?.decision || "WAIT";

  const decisionColor =
    decision === "BUY" ? "#10B981" : decision === "SELL" ? "#EF4444" : "#F59E0B";

  const reevaluate = async () => {
    const b = Number(snapshot?.market?.bid || 4369);
    setBusy(true);
    try {
      const side = decision === "BUY" ? "BUY" : "SELL";
      const payload =
        side === "BUY"
          ? { ...DEFAULT_ADVICE, side: "BUY", openLow: b - 1, openHigh: b + 1, sl: b - 6, tps: [b + 8, b + 14, b + 22, b + 30] }
          : { ...DEFAULT_ADVICE, side: "SELL", openLow: b - 1, openHigh: b + 1, sl: b + 6, tps: [b - 8, b - 14, b - 22, b - 30] };
      const res = await postAdvice(payload);
      if (res.errors) {
        toast.error(res.errors.join(", "));
      } else {
        setOverride(res);
        toast.success("Consenso AI ricalcolato");
      }
    } catch (_) {
      toast.error("AI non disponibile");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div id="ai-consensus" data-testid="ai-consensus-panel" className="panel p-5 space-y-5 float-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          <span className="w-6 h-6 rounded-md grid place-items-center bg-sky-500/15 text-sky-400">
            <Cpu size={13} />
          </span>
          AI Consensus Engine
        </div>
        <button
          data-testid="ai-reevaluate-button"
          onClick={reevaluate}
          disabled={busy}
          className="flex items-center gap-1.5 text-[10px] px-2.5 py-1.5 rounded-md bg-[#0F1728] border border-white/10 text-slate-300 hover:text-slate-100 disabled:opacity-60"
        >
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Rivaluta
        </button>
      </div>

      <div className="flex items-center gap-4">
        <div
          className="w-[86px] h-[86px] rounded-full grid place-items-center relative shrink-0"
          style={{ background: `conic-gradient(${decisionColor} ${score}%, #1a2438 ${score}% 100%)` }}
        >
          <div className="absolute inset-[7px] rounded-full bg-[#101a2b] grid place-items-center">
            <span className="font-mono text-xl font-extrabold">{score}%</span>
          </div>
        </div>
        <div className="min-w-0">
          <div
            data-testid="ai-overall-decision-badge"
            className="font-mono text-lg font-extrabold tracking-tight"
            style={{ color: decisionColor }}
          >
            {decision} XAUUSD
          </div>
          <p className="text-[10px] text-slate-400 leading-relaxed mt-1">
            {ai?.reason || "Nessuna analisi ricevuta. Avvia una rivalutazione o invia un segnale."}
          </p>
          <p className="text-[10px] text-slate-500 mt-1 font-mono">
            Confidenza {confidence}% · {models.filter((m) => !m.error).length} modelli ·{" "}
            <span data-testid="ai-consensus-source" className={ai?.source === "live" ? "text-emerald-400" : "text-amber-400"}>
              {ai?.source === "live" ? "LIVE" : "SIMULATO"}
            </span>
          </p>
        </div>
      </div>

      <div className="grid gap-2">
        {models.length ? (
          models.map((m, i) => {
            const dec = m.error ? "ERROR" : m.decision || "WAIT";
            const c = dec === "BUY" ? "text-emerald-400" : dec === "SELL" ? "text-rose-400" : "text-amber-400";
            return (
              <div
                key={i}
                className="flex items-center justify-between bg-white/[0.025] border border-white/8 rounded-lg px-3 py-2"
              >
                <div className="flex items-center gap-2 text-[11px] text-slate-300">
                  <span className="w-5 h-5 rounded grid place-items-center bg-[#22344a] text-sky-300 text-[9px] font-bold">
                    A{i + 1}
                  </span>
                  {m.provider}
                </div>
                <span className={`text-[10px] font-mono font-bold ${c}`}>
                  {dec}
                  {Number.isFinite(Number(m.confidence)) ? ` · ${Math.round(m.confidence * 100)}%` : ""}
                </span>
              </div>
            );
          })
        ) : (
          <div className="text-[11px] text-slate-500 text-center py-4">Nessun modello disponibile</div>
        )}
      </div>
    </div>
  );
}
